import { useCallback, useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

export interface PipelineToleranceConfig {
  capture_missing_pages_pct: number;
  capture_missing_pages_abs: number;
  capture_timeout_treat_as_pending: boolean;
  manifest_recheck_attempts: number;
  manifest_recheck_delay_ms: number;
  ocr_scope_unstable_grace_sec: number;
  ocr_fanout_rate_limit_tolerance_pct: number;
  signals_retry_after_ms: number;
}

export const DEFAULT_PIPELINE_TOLERANCES: PipelineToleranceConfig = {
  capture_missing_pages_pct: 0,
  capture_missing_pages_abs: 0,
  capture_timeout_treat_as_pending: true,
  manifest_recheck_attempts: 3,
  manifest_recheck_delay_ms: 1200,
  ocr_scope_unstable_grace_sec: 0,
  ocr_fanout_rate_limit_tolerance_pct: 0,
  signals_retry_after_ms: 15000,
};

function coerceNumber(value: unknown, fallback: number): number {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
}

function normalizeConfig(input: unknown): PipelineToleranceConfig {
  const source = (input && typeof input === "object") ? input as Record<string, unknown> : {};
  return {
    capture_missing_pages_pct: Math.min(1, Math.max(0, coerceNumber(source.capture_missing_pages_pct, DEFAULT_PIPELINE_TOLERANCES.capture_missing_pages_pct))),
    capture_missing_pages_abs: Math.max(0, Math.floor(coerceNumber(source.capture_missing_pages_abs, DEFAULT_PIPELINE_TOLERANCES.capture_missing_pages_abs))),
    capture_timeout_treat_as_pending: source.capture_timeout_treat_as_pending !== false,
    manifest_recheck_attempts: Math.max(0, Math.floor(coerceNumber(source.manifest_recheck_attempts, DEFAULT_PIPELINE_TOLERANCES.manifest_recheck_attempts))),
    manifest_recheck_delay_ms: Math.max(0, Math.floor(coerceNumber(source.manifest_recheck_delay_ms, DEFAULT_PIPELINE_TOLERANCES.manifest_recheck_delay_ms))),
    ocr_scope_unstable_grace_sec: Math.max(0, Math.floor(coerceNumber(source.ocr_scope_unstable_grace_sec, DEFAULT_PIPELINE_TOLERANCES.ocr_scope_unstable_grace_sec))),
    ocr_fanout_rate_limit_tolerance_pct: Math.min(1, Math.max(0, coerceNumber(source.ocr_fanout_rate_limit_tolerance_pct, DEFAULT_PIPELINE_TOLERANCES.ocr_fanout_rate_limit_tolerance_pct))),
    signals_retry_after_ms: Math.max(1000, Math.floor(coerceNumber(source.signals_retry_after_ms, DEFAULT_PIPELINE_TOLERANCES.signals_retry_after_ms))),
  };
}

function deepEqual(a: unknown, b: unknown): boolean {
  return JSON.stringify(a) === JSON.stringify(b);
}

export function usePipelineTolerances() {
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [flagEnabled, setFlagEnabled] = useState(false);
  const [storedConfig, setStoredConfig] = useState<PipelineToleranceConfig>(DEFAULT_PIPELINE_TOLERANCES);
  const [draftConfig, setDraftConfig] = useState<PipelineToleranceConfig>(DEFAULT_PIPELINE_TOLERANCES);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: fetchErr } = await supabase
        .from("app_config")
        .select("key, value_json")
        .in("key", ["pipeline_tolerances_v1", "S1_PIPELINE_TOLERANCES_V1"]);

      if (fetchErr) throw fetchErr;

      const toleranceRow = (data ?? []).find((r) => r.key === "pipeline_tolerances_v1");
      const flagRow = (data ?? []).find((r) => r.key === "S1_PIPELINE_TOLERANCES_V1");

      const normalized = normalizeConfig(toleranceRow?.value_json ?? DEFAULT_PIPELINE_TOLERANCES);
      const enabled = Boolean(flagRow?.value_json === true);

      setStoredConfig(normalized);
      setDraftConfig(normalized);
      setFlagEnabled(enabled);
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Failed to load tolerance policy";
      setError(msg);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const isDirty = useMemo(() => !deepEqual(storedConfig, draftConfig), [storedConfig, draftConfig]);

  const updateField = useCallback(<K extends keyof PipelineToleranceConfig>(key: K, value: PipelineToleranceConfig[K]) => {
    setDraftConfig((prev) => ({ ...prev, [key]: value }));
  }, []);

  const resetDraft = useCallback(() => {
    setDraftConfig(storedConfig);
  }, [storedConfig]);

  const save = useCallback(async () => {
    setSaving(true);
    setError(null);
    try {
      const next = normalizeConfig(draftConfig);
      const prev = storedConfig;

      const { error: upsertErr } = await supabase
        .from("app_config")
        .upsert({ key: "pipeline_tolerances_v1", value_json: next as any, updated_at: new Date().toISOString() });
      if (upsertErr) throw upsertErr;

      // Best-effort audit insert (new table from CR730 migration)
      try {
        const sb = supabase as any;
        const {
          data: { user },
        } = await supabase.auth.getUser();

        await sb
          .from("pipeline_tolerance_policy_audit")
          .insert({
            changed_by: user?.email ?? user?.id ?? null,
            action: "PIPELINE_TOLERANCE_UPDATED",
            before_json: prev,
            after_json: next,
            metadata_json: { source: "admin.pipeline-tolerances" },
          });
      } catch {
        // non-fatal audit failure
      }

      setStoredConfig(next);
      setDraftConfig(next);
      toast({ title: "Tolerance policy saved", description: "Pipeline tolerance settings updated." });
      return true;
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Failed to save tolerance policy";
      setError(msg);
      toast({ title: "Save failed", description: msg, variant: "destructive" });
      return false;
    } finally {
      setSaving(false);
    }
  }, [draftConfig, storedConfig, toast]);

  return {
    loading,
    saving,
    error,
    flagEnabled,
    storedConfig,
    draftConfig,
    isDirty,
    updateField,
    resetDraft,
    save,
    refresh: load,
  };
}
